import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    FormControl, InputLabel, Select, MenuItem, Dialog, TextField as MuiTextField, Typography, Button, DialogTitle,
    DialogContent, DialogActions, RadioGroup, Radio, FormControlLabel, FormGroup, Checkbox, Input, useMediaQuery
} from '@material-ui/core';
import {
    List, Datagrid, TextField, FunctionField, Button as RaButton, Filter, TextInput, SimpleList
} from 'react-admin';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { translate, crudUpdate } from 'ra-core';
import { customTheme } from "./customTheme";
import config from './config.json';
import EditIcon from '@material-ui/icons/Edit';
import ParkingSearchSelector from "./components/ParkingSearchSelector";
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import moment from "moment";
import { saveTokens } from './utils/saveTokens';
import notify from './components/CustomUseNotify';

const useStyles = makeStyles({
    list: {
        marginBottom: 40,
    },
    loaderContainer: {
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
    },
    parkingSelectorContainer: {
        display: "grid",
        justifyContent: "space-between",
        maxWidth: 1000,
    },
    asyncSelector: {
        display: "grid",
        gridAutoFlow: "column",
        justifyContent: "space-between",
        paddingRight: 5,
    },
    parkingListHead: {
        color: "#777",
    },
    errorBackground: {
        backgroundColor: customTheme.palette.error.main,
        color: "#fff",
        '&:hover': {
            backgroundColor: "#be403e",
        }
    },
    errorText: {
        color: customTheme.palette.error.main,
    },
    checkIcon: {
        fontSize: 18,
        color: customTheme.palette.primary.main,
        marginRight: 6,
    },
    crossIcon: {
        fontSize: 18,
        color: customTheme.palette.error.main,
        marginRight: 6,
    }
});

const renderCollected = (row, source) => row[source] === 1 ? "Yes" : "No";

export function DeviceIssuesList(props) {
    const classes = useStyles();
    const isSmallDevice = useMediaQuery(theme => customTheme.breakpoints.down("xs"));

    const [currentParking, setCurrentParking] = React.useState(null);
    const [reasons, setReasons] = React.useState({ "Return": [], "Replace": [] });

    const zoneIds = currentParking && currentParking.zone_ids ? currentParking.zone_ids.split(",") : null;
    const zoneLabels = currentParking && currentParking.zones ? currentParking.zones.split(";") : null;
    const finalZones = zoneIds && zoneLabels ? zoneIds.map((id, index) => (
        { id, name: zoneLabels[index] }
    )) : [];
    if (finalZones)
        finalZones.sort((a, b) => (a.name > b.name ? 1 : (a.name < b.name ? -1 : 0)));

    const fetchReasons = () => {
        fetch(`${config.HOST_NAME}/warrior/commonData`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
            },
        })
            .then(response => {
                saveTokens(response)
                return response.json()
            })
            .then(json => {
                if (json.DEVICE_DEPLOYMENT_REASONS)
                    setReasons(json.DEVICE_DEPLOYMENT_REASONS);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const onParkingChange = (parking) => {
        setCurrentParking({ ...parking, allZones: true });
    };

    const onZoneChange = (event) => {
        setCurrentParking({
            ...currentParking,
            id: event.target.value,
            name: zoneLabels[zoneIds.findIndex(id => id === event.target.value)],
            allZones: event.target.value === "All"
        });
    };

    React.useEffect(() => {
        fetchReasons();
    }, []);

    return (<div>
        <div className={classes.parkingSelectorContainer} style={{ gridAutoFlow: isSmallDevice ? "row" : "column", gridTemplateColumns: isSmallDevice ? "100%" : "48% 48%", gridRowGap: isSmallDevice ? 18 : 0, margin: isSmallDevice ? "0 16px" : 0 }}>
            <ParkingSearchSelector
                value={currentParking}
                onChange={onParkingChange}
                clearable={true}
            />

            {currentParking && currentParking.zone_ids && currentParking.zone_ids.includes(',') &&
                <FormControl variant="outlined">
                    <InputLabel>Zone Name</InputLabel>
                    <Select
                        value={currentParking.allZones ? "All" : currentParking.id}
                        onChange={onZoneChange}
                        label="Zone Name"
                        variant="outlined"
                    >
                        <MenuItem value="All">All</MenuItem>
                        {finalZones.map((zone, index) => (
                            <MenuItem key={index} value={zone.id}>{zone.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            }
        </div>

        <List
            {...props}
            title="Device Issues"
            bulkActionButtons={false}
            exporter={false}
            sort={{ field: "start_time", order: "DESC" }}
            filters={<Filter {...props}>
                <TextInput variant="outlined" label="Search Serial Number" source="serial_number" alwaysOn />
            </Filter>}
            filter={{ access_key_id: currentParking ? currentParking.id : undefined, zone_id: currentParking && currentParking.allZones ? currentParking.zone_id : undefined }}
        >
            {isSmallDevice ?
                <SimpleList
                    primaryText={record => `${record.serial_number}${record["key_serial_number"] ? ` (${record["key_serial_number"]})` : ""}`}
                    secondaryText={record => (<div>
                        <div style={{ color: customTheme.palette.secondary.main }}>{record["parking_name"]}</div>
                        <div><span style={{ color: customTheme.palette.error.main }}>{record["operation"]}</span> by {record["warrior_name"]}</div>
                        <div>Reason: {record["reasons"]}</div>
                        {record["reasons"] && record["reasons"] !== "" ? <div>Comments: {record["reasons"]}</div> : ""}
                        <div style={{ display: "flex", alignItems: "center", color: "#555" }}>
                            {record["charger_collected"] === 1 ? <CheckIcon className={classes.checkIcon} /> : <ClearIcon className={classes.crossIcon} />} Charger
                            <div style={{ flex: 1, minWidth: 10 }} />
                            {record["charging_wire_collected"] === 1 ? <CheckIcon className={classes.checkIcon} /> : <ClearIcon className={classes.crossIcon} />} Charging Wire
                        </div>
                        <div>{moment(record["start_time"], "YYYY-MM-DD HH:mm:ss").format("Do MMM, YYYY h:mm:ss A")}</div>
                        <div style={{ display: "flex", rowGap: 10 }}>
                            <CustomApprovalButton {...props} record={record} label="Status" source="resolved" />
                            <CustomEditButton {...props} record={record} reasons={reasons} />
                        </div>
                    </div>)}
                    //rightIcon={record => <div style={{ position: "relative", right: -17 }}><CustomDeleteButton {...props} record={record} /></div>}
                    linkType={false}
                />
                :
                <Datagrid classes={{ headerCell: classes.parkingListHead }}>
                    <TextField label="Serial Number" source="serial_number" />
                    <TextField label="Key Serial" source="key_serial_number" />
                    <TextField label="Parking Name" source="parking_name" />
                    <TextField label="Access Key" source="access_key" />
                    <TextField label="Operation" source="operation" />
                    <FunctionField label="Charger" render={row => renderCollected(row, "charger_collected")} />
                    <FunctionField label="Charging Wire" render={row => renderCollected(row, "charging_wire_collected")} />
                    <TextField label="Reasons" source="reasons" />
                    <TextField label="Comments" source="comments" />
                    <TextField label="Time" source="start_time" />
                    <TextField label="Warrior Name" source="warrior_name" />
                    <CustomEditButton reasons={reasons} />
                    <CustomApprovalButton label="Status" source="resolved" />
                </Datagrid>
            }
        </List>
    </div>);
};

function EditButton(props) {
    //const notify = useNotify();
    const isSmallDevice = useMediaQuery(theme => customTheme.breakpoints.down("xs"));

    const [dialogVisible, setDialogVisible] = React.useState(false);
    const [operation, setOperation] = React.useState(null);
    const [chargerCollected, setChargerCollected] = React.useState(false);
    const [chargingWireCollected, setChargingWireCollected] = React.useState(false);
    const [reasons, setReasons] = React.useState([]);
    const [comments, setComments] = React.useState();
    const [valuesChanged, setValuesChanged] = React.useState(false);

    const openDialog = () => {
        setOperation(props.record.operation);
        setChargerCollected(props.record.charger_collected);
        setChargingWireCollected(props.record.charging_wire_collected);
        setReasons(props.reasons[props.record.operation].filter(item => props.record.reasons.includes(item.label)).map(item => item.label));
        setComments(props.record.comments);
        setValuesChanged(false);
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
    };

    const handleUpdate = (event) => {
        event.preventDefault();

        const { dispatchCrudUpdate, resource, record, basePath } = props;
        if (reasons.length === 0) {
            notify("No Reason Selected", 'warning');
            return;
        }

        const data = {
            newData: {
                operation,
                charger_collected: chargerCollected,
                charging_wire_collected: chargingWireCollected,
                reasons: reasons.join(", "),
                comments: reasons.includes(props.reasons[operation][props.reasons[operation].length - 1].label) ? comments : "",
            },
            oldData: record,
        };
        dispatchCrudUpdate(resource, record.id, data, record, basePath, "/deviceIssues");
        setDialogVisible(false);
    };

    const handleOperationChange = event => {
        setOperation(event.target.value);
        setReasons([]);
        setValuesChanged(true);
    };

    const handleChargerChange = event => {
        setChargerCollected(event.target.checked)
        setValuesChanged(true);
    };

    const handleChargingWireChange = event => {
        setChargingWireCollected(event.target.checked)
        setValuesChanged(true);
    };

    const handleReasonsChange = (event) => {
        setReasons(event.target.value);
        setValuesChanged(true);
    };

    const handleCommentsChange = (event) => {
        setComments(event.target.value);
        setValuesChanged(true);
    };

    return (
        <>
            {props.record.resolved === -1 && <RaButton onClick={openDialog} label="Edit">
                <EditIcon />
            </RaButton>}

            <Dialog fullWidth open={dialogVisible} onClose={closeDialog}>
                <DialogTitle>Edit Device Issue</DialogTitle>
                <DialogContent>
                    <RadioGroup row value={operation} onChange={handleOperationChange}>
                        <FormControlLabel value="Return" control={<Radio color="primary" />} label="Return" />
                        <FormControlLabel value="Replace" control={<Radio color="primary" />} label="Replace" />
                    </RadioGroup>

                    <FormGroup row style={{ marginTop: 5 }}>
                        <FormControlLabel control={<Checkbox checked={chargerCollected} onChange={handleChargerChange} color="primary" />} label="Charger" />
                        <FormControlLabel control={<Checkbox checked={chargingWireCollected} onChange={handleChargingWireChange} color="primary" />} label="Charging Wire Collected" />
                    </FormGroup>

                    <FormControl style={{ minWidth: isSmallDevice ? 0 : 200, marginTop: 12, width: isSmallDevice ? "100%" : "auto" }}>
                        <InputLabel>Reasons</InputLabel>
                        <Select
                            multiple
                            value={reasons}
                            onChange={handleReasonsChange}
                            input={<Input />}
                        >
                            {operation && props.reasons[operation].map((item, index) => (
                                <MenuItem key={index} value={item.label}>{item.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {operation && reasons.includes(props.reasons[operation][props.reasons[operation].length - 1].label) &&
                        <FormControl style={{ display: "block", marginTop: 15 }}>
                            <MuiTextField placeholder="Describe" variant="outlined" size="small" value={comments} onChange={handleCommentsChange} />
                        </FormControl>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={handleUpdate} disabled={!valuesChanged}>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

EditButton.defaultProps = {
    redirect: 'list',
    undoable: false
};

const CustomEditButton = compose(
    connect(
        null,
        { dispatchCrudUpdate: crudUpdate }
    ),
    translate
)(EditButton);

function ApprovalButton(props) {
    const classes = useStyles();
    const { record, source } = props;
    const isSmallDevice = useMediaQuery(theme => customTheme.breakpoints.down("xs"));

    const [resolved, setResolved] = React.useState(null);
    const [dialogVisible, setDialogVisible] = React.useState(false);

    const openDialog = state => {
        setResolved(state);
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
    };

    const handleUpdate = (event) => {
        event.preventDefault();
        closeDialog();
        const { dispatchCrudUpdate, resource, basePath } = props;
        const data = {
            newData: {
                resolved,
            },
            oldData: record,
        };
        dispatchCrudUpdate(resource, record.id, data, record, basePath, "/deviceIssues");
    };

    return (
        <div style={{ textAlign: isSmallDevice ? "left" : "center", display: isSmallDevice ? "flex" : "block", flex: 1 }}>
            {record[source] === -1 ?
                <>
                    <Button variant="contained" color="primary" style={{ marginTop: isSmallDevice ? 10 : 0 }} onClick={() => openDialog(1)}>Approve</Button>
                    <Button variant="contained" style={{ marginTop: 10, marginLeft: isSmallDevice ? 10 : 0 }} className={classes.errorBackground} onClick={() => openDialog(0)}>Decline</Button>
                </>
                : <>
                    <Typography style={{ fontWeight: "bold", color: record[source] === 1 ? customTheme.palette.primary.main : customTheme.palette.error.main }}>{record[source] === 1 ? "Approved" : record[source] === 0 && "Declined"}</Typography>
                    {record.approved_by && `by ${record.approved_by}`}
                </>
            }

            <Dialog fullWidth open={dialogVisible} onClose={closeDialog}>
                <DialogContent>Are you sure you want to <span style={{ fontWeight: "bold" }}>{resolved === 1 ? "Approve" : "Decline"}</span></DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button className={resolved !== 1 ? classes.errorText : null} color={resolved === 1 ? "primary" : "inherit"} onClick={handleUpdate}>{resolved === 1 ? "Approve" : "Decline"}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

ApprovalButton.defaultProps = {
    redirect: 'list',
    undoable: false
};

const CustomApprovalButton = compose(
    connect(
        null,
        { dispatchCrudUpdate: crudUpdate }
    ),
    translate
)(ApprovalButton);